import {
	addUnit,
	endOf,
	formatDate,
	isBefore,
	startOf,
	utcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"

/**
 * What a user's cell in the day-wise logins table stands for.
 */
export type TDayLoginCell = {
	/** the account did not exist that day (before signup / after deletion) */
	outside_account: boolean
	signed_up: boolean
	deleted: boolean
	logins: number
	/** countries other than India logged in from, sorted */
	foreign_countries: Array<string>
}

/**
 * Work out a user's cell for one day. Days are compared as "YYYY-MM-DD"
 * strings in local time; signupDay / deletedDay are null when not set.
 */
export function dayLoginCell({
	day,
	signupDay,
	deletedDay,
	countryCodes,
}: {
	day: string
	signupDay: string | null
	deletedDay: string | null
	/** country of each login that day (null when unknown) */
	countryCodes: Array<string | null | undefined>
}): TDayLoginCell {
	return {
		outside_account: Boolean(
			(signupDay && day < signupDay) || (deletedDay && day > deletedDay)
		),
		signed_up: signupDay === day,
		deleted: deletedDay === day,
		logins: countryCodes.length,
		foreign_countries: Array.from(
			new Set(
				countryCodes.filter(
					(code): code is string => Boolean(code) && code !== "IN"
				)
			)
		).sort(),
	}
}

/**
 * Day-by-day logins of a month: the days (up to today), each user's cell
 * per day and how many users logged in on each day. Timestamps are UTC and
 * bucketed into local days.
 */
export function buildDaywiseLogins({
	month,
	users,
	logins,
	today = new Date(),
	newestFirst = false,
}: {
	month: Date
	users: Array<{ id: number; signup_at?: string; deleted_at?: string }>
	logins: Array<{
		user_id: number
		created_at: string
		country_code?: string | null
	}>
	today?: Date
	/** latest day first (days, cells and totals alike) */
	newestFirst?: boolean
}): {
	days: Array<Date>
	cellsByUser: Record<number, Array<TDayLoginCell>>
	totals: Array<number>
} {
	const days: Array<Date> = []
	const monthEnd = endOf(month, "month")
	const todayEnd = endOf(today, "day")
	let date = startOf(month, "month")
	while (isBefore(date, todayEnd) && isBefore(date, monthEnd)) {
		days.push(date)
		date = addUnit(date, 1, "day")
	}
	const toDay = (timestamp?: string) =>
		timestamp
			? formatDate(utcTimestampToLocalDate(timestamp), "YYYY-MM-DD")
			: null
	const countriesByUserDay = new Map<string, Array<string | null | undefined>>()
	logins.forEach((login) => {
		const key = `${login.user_id}|${toDay(login.created_at)}`
		countriesByUserDay.set(key, [
			...(countriesByUserDay.get(key) || []),
			login.country_code,
		])
	})
	const cellsByUser: Record<number, Array<TDayLoginCell>> = {}
	users.forEach((user) => {
		const signupDay = toDay(user.signup_at)
		const deletedDay = toDay(user.deleted_at)
		cellsByUser[user.id] = days.map((date) => {
			const day = formatDate(date, "YYYY-MM-DD")
			return dayLoginCell({
				day,
				signupDay,
				deletedDay,
				countryCodes: countriesByUserDay.get(`${user.id}|${day}`) || [],
			})
		})
	})
	const totals = days.map(
		(_, index) =>
			users.filter((user) => cellsByUser[user.id][index].logins).length
	)
	if (newestFirst) {
		days.reverse()
		totals.reverse()
		Object.values(cellsByUser).forEach((cells) => cells.reverse())
	}
	return { days, cellsByUser, totals }
}
